import { useTranslation } from "react-i18next";
import React, { Suspense, useEffect } from "react";
import LogoText from "../assets/logo_text.svg?react";
import { Field, FieldGroup, Fieldset, Label } from "./base/fieldset";
import { BackButton, Button } from "src/components/base/button";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { toast } from "react-toastify/unstyled";
import { AnimatePresence, motion } from "framer-motion";
import { ApiPublic } from "src/api/api_public";
import { ArrowLeftIcon, EyeIcon, EyeSlashIcon, FingerPrintIcon } from "@heroicons/react/20/solid";
import { Subheading } from "src/components/base/heading";
import { StartLoginResponse } from "src/api/generated/frontend_public_v1";
import { useNavigate } from "@tanstack/react-router";
import { authenticate } from "src/api/auth";
import { AuthLayout } from "src/components/base/auth-layout";
import ResetCredentials from "src/components/reset";
import { Text } from "src/components/base/text";
import { PREFERRED_LOGIN } from "src/utils/local-storage";

/**
 * The properties for {@link Login}
 */
export type LoginProps = {
    /** The function that should be executed on a successful login */
    onLogin: () => void;
};

/** Step the {@link Login} component is in */
type LoginStep = "selection" | "password" | "totp" | "password-less" | "forgot-pw" | "password-reset";

/**
 * The login component
 */
export default function Login(props: LoginProps) {
    const [t] = useTranslation("login");

    const navigate = useNavigate();

    const [loginStep, setLoginStep] = React.useState<LoginStep>("selection");
    const [tailscaleToken, setTailscaleToken] = React.useState<string | undefined>(undefined);

    // State for local login flow
    const [mail, setMail] = React.useState("");
    const [flows, setFlows] = React.useState<StartLoginResponse | undefined>(undefined);
    const [password, setPassword] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);
    const [totp, setTotp] = React.useState("");

    const submitMail = async (mail: string) => {
        const res = await ApiPublic.vpnAuth.startLogin({ mail: mail });
        if (res.result === "Ok") {
            setMail(mail);
            setFlows(res.value);
            if (res.value.key) {
                setLoginStep("password-less");
            } else if (res.value.password) {
                setLoginStep("password");
            }
        } else if (res.result === "Err") {
            if (res.error.invalid_identifier) {
                toast.error(t("toast.unknown-user"));
            }
        }
    };

    const submitPassword = async () => {
        const res = await ApiPublic.vpnAuth.loginPassword({ password: password });
        if (res.result === "Ok") {
            if (res.value.res === "NeedReset") {
                toast.info(t("toast.password-reset-required"));
                setLoginStep("password-reset");
            } else if (res.value.res === "NeedTotp") {
                setLoginStep("totp");
            } else if (res.value.res === "Finished") {
                toast.success(t("toast.login-successful"));
                props.onLogin();
            }
        } else if (res.result === "Err") {
            if (res.error.mail) {
                toast.error(t("toast.unknown-user"));
            }
            if (res.error.password) {
                toast.error(t("toast.invalid-password"));
            }
        }
    };

    const submitTotp = async () => {
        const res = await ApiPublic.vpnAuth.verifyTotp({ token: totp });
        if (res.result === "Ok") {
            if (res.value === "NeedReset") {
                toast.info(t("toast.password-reset-required"));
                setLoginStep("password-reset");
            } else if (res.value === "Finished") {
                toast.success(t("toast.login-successful"));
                props.onLogin();
            }
        } else if (res.result === "Err") {
            if (res.error.invalid_token) {
                toast.error(t("toast.token-invalid"));
            }
        }
    };

    const performWebauthnLogin = async () => {
        const res = await authenticate();
        if (res.result === "Ok") {
            if (res.value === "NeedReset") {
                toast.info(t("toast.password-reset-required"));
                setLoginStep("password-reset");
            } else if (res.value === "Finished") {
                toast.success(t("toast.login-successful"));
                props.onLogin();
            }
        } else if (res.result === "Err") {
            if (res.error.unknown) {
                toast.error(t("toast.unknown-error"));
            }
        }
    };

    /**
     * Reset the local login flow
     */
    const reset = () => {
        setLoginStep("selection");
        setMail("");
        setPassword("");
        setTotp("");
        setFlows(undefined);
    };

    useEffect(() => {
        const pathName = window.location.pathname;
        if (pathName.startsWith("/u/tailscale-login/")) {
            const token = pathName.split("/")[3];
            setTailscaleToken(token);
        }
    }, []);

    return (
        <AuthLayout>
            <div className={"flex flex-col items-center justify-center"}>
                <LogoText
                    className={
                        "h-24 max-w-full p-3 text-[#233F94] drop-shadow-lg dark:text-white dark:drop-shadow-none"
                    }
                />
                <div className={"mt-12 w-full overflow-hidden p-1"}>
                    <AnimatePresence initial={false} mode={"wait"}>
                        <div className={"flex items-center justify-center"}>
                            {loginStep === "selection" && (
                                <motion.div
                                    className={"w-full max-w-sm"}
                                    key={"selection"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <Form onSubmit={() => submitMail(mail)}>
                                        <Fieldset>
                                            <FieldGroup>
                                                {tailscaleToken && (
                                                    <BackButton
                                                        href={"/s/login/$token"}
                                                        params={{ token: tailscaleToken }}
                                                        onClick={() => PREFERRED_LOGIN.remove()}
                                                    >
                                                        {t("button.back-button-tailscale")}
                                                    </BackButton>
                                                )}
                                                <Field>
                                                    <Label>{t("label.mail-login")}</Label>
                                                    <Input
                                                        required={true}
                                                        autoComplete={"email"}
                                                        type={"email"}
                                                        placeholder={t("label.your-email")}
                                                        value={mail}
                                                        onChange={(e) => setMail(e.target.value)}
                                                    />
                                                </Field>

                                                <Button outline={true} type={"submit"} className={"mb-8 w-full"}>
                                                    {t("button.next")}
                                                </Button>
                                            </FieldGroup>
                                        </Fieldset>
                                    </Form>
                                </motion.div>
                            )}

                            {loginStep === "password" && (
                                <motion.div
                                    className={"flex w-full max-w-sm flex-col gap-4"}
                                    key={"password"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <div className={"flex flex-col gap-1"}>
                                        <Subheading>
                                            {t("label.login-as")} {mail}
                                        </Subheading>
                                        <Button plain={true} onClick={() => reset()} className={"w-fit"}>
                                            <ArrowLeftIcon />
                                            {t("button.use-different-login")}
                                        </Button>
                                    </div>
                                    <Form onSubmit={() => submitPassword()}>
                                        {/* This input field helps the fill in features of the browser */}
                                        <Input
                                            type={"email"}
                                            className={"hidden"}
                                            value={mail}
                                            readOnly={true}
                                            autoComplete={"email"}
                                        />
                                        <Field>
                                            <Label>{t("label.password")}</Label>
                                            <div className={"mt-3 flex gap-3"}>
                                                <Input
                                                    autoFocus={true}
                                                    type={showPassword ? "text" : "password"}
                                                    autoComplete={"current-password"}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                />
                                                <Button plain={true} onClick={() => setShowPassword((prev) => !prev)}>
                                                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                                </Button>
                                            </div>
                                        </Field>

                                        <Button
                                            plain={true}
                                            className={"mt-3 !text-xs font-medium text-white dark:text-zinc-300"}
                                            onClick={() => setLoginStep("forgot-pw")}
                                        >
                                            {t("button.forgot-pw")}
                                        </Button>

                                        <Button
                                            type={"submit"}
                                            outline={true}
                                            className={"mt-5 w-full"}
                                            onClick={() => setPassword(password)}
                                        >
                                            {t("button.next")}
                                        </Button>
                                    </Form>
                                </motion.div>
                            )}

                            {loginStep === "totp" && (
                                <motion.div
                                    key={"totp"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <Form onSubmit={submitTotp}>
                                        <FieldGroup>
                                            <Subheading>{t("heading.mfa-needed")}</Subheading>
                                            <Text>{t("description.mfa-needed")}</Text>
                                            <Field>
                                                <Label>{t("label.totp-token")}</Label>
                                                <Input
                                                    required={true}
                                                    type={"number"}
                                                    autoComplete={"one-time-code"}
                                                    value={totp}
                                                    onChange={(e) => setTotp(e.target.value)}
                                                />
                                            </Field>

                                            <Button type={"submit"} color={"blue"} className={"w-full"}>
                                                {t("button.sign-in")}
                                            </Button>
                                        </FieldGroup>
                                    </Form>
                                </motion.div>
                            )}

                            {loginStep === "password-less" && (
                                <motion.div
                                    key={"password-less"}
                                    className={"flex w-full max-w-sm flex-col gap-2"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <Subheading>
                                        {t("label.login-as")} {mail}
                                    </Subheading>
                                    <Button plain={true} onClick={() => reset()} className={"w-fit"}>
                                        <ArrowLeftIcon />
                                        {t("button.use-different-login")}
                                    </Button>

                                    <Button color={"blue"} className={"mt-3 w-full"} onClick={performWebauthnLogin}>
                                        <FingerPrintIcon />
                                        {t("button.login-security-key")}
                                    </Button>

                                    {flows?.password && (
                                        <Button
                                            plain={true}
                                            onClick={() => setLoginStep("password")}
                                            className={"w-fit"}
                                        >
                                            {t("button.use-password")}
                                        </Button>
                                    )}
                                </motion.div>
                            )}

                            {loginStep === "forgot-pw" && (
                                <motion.div
                                    key={"forgot-pw"}
                                    className={"w-full max-w-sm"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <div className={"flex flex-col gap-3"}>
                                        <Button
                                            plain={true}
                                            className={"w-fit !text-xs font-medium text-white dark:text-zinc-300"}
                                            onClick={() => setLoginStep("password")}
                                        >
                                            <ArrowLeftIcon />
                                            {t("button.back")}
                                        </Button>
                                        <Subheading>{t("button.forgot-pw")}</Subheading>
                                        <p>{t("description.forgot-pw")}</p>
                                    </div>
                                </motion.div>
                            )}

                            {loginStep === "password-reset" && (
                                <motion.div
                                    key={"password-reset"}
                                    className={"w-full"}
                                    initial={{ opacity: 0, x: 300 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -300 }}
                                >
                                    <Suspense>
                                        <ResetCredentials
                                            onSubmit={async function () {
                                                toast.success(t("toast.login-successful"));
                                                props.onLogin();
                                                await navigate({ to: "/u/dashboard" });
                                            }}
                                        />
                                    </Suspense>
                                </motion.div>
                            )}
                        </div>
                    </AnimatePresence>
                </div>
            </div>
        </AuthLayout>
    );
}
